import React from 'react'
import { Toast } from 'react-hot-toast'
import { AppButton } from 'components/Common/AppButton/AppButton.component'

interface AppCustomToastProps {
  t: Toast
  variant?: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  icon?: React.ReactNode
  actionButton?: {
    label: string
    onClick: () => void
  }
  onDismiss?: () => void
}

const getVariantStyles = (variant: string) => {
  switch (variant) {
    case 'success':
      return {
        bgColor: 'bg-app-green-10',
        borderColor: 'ring-app-green-30',
        accentColor: 'bg-app-green-60',
        iconColor: 'text-app-green-70',
      }
    case 'error':
      return {
        bgColor: 'bg-app-red-10',
        borderColor: 'ring-app-red-30',
        accentColor: 'bg-app-red-60',
        iconColor: 'text-app-red-70',
      }
    case 'warning':
      return {
        bgColor: 'bg-app-orange-10',
        borderColor: 'ring-app-orange-30',
        accentColor: 'bg-app-orange-60',
        iconColor: 'text-app-orange-70',
      }
    case 'info':
    default:
      return {
        bgColor: 'bg-app-primary-5',
        borderColor: 'ring-app-primary-30',
        accentColor: 'bg-app-primary-60',
        iconColor: 'text-app-primary-60',
      }
  }
}

const getDefaultIcon = (variant: string) => {
  switch (variant) {
    case 'success':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      )
    case 'error':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      )
    case 'warning':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      )
    case 'info':
    default:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      )
  }
}

export const AppCustomToast: React.FC<AppCustomToastProps> = ({
  t,
  variant = 'info',
  title,
  message,
  icon,
  actionButton,
  onDismiss,
}) => {
  const styles = getVariantStyles(variant)
  const defaultIcon = getDefaultIcon(variant)

  const handleDismiss = (e?: React.MouseEvent) => {
    // Stop propagation to prevent modal backdrop from intercepting click
    e?.stopPropagation()
    if (onDismiss) {
      onDismiss()
    }
  }

  const handleActionClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent modal backdrop from intercepting click
    e.stopPropagation()
    // Dismiss the toast first, then execute the action
    handleDismiss()
    if (actionButton?.onClick) {
      actionButton.onClick()
    }
  }

  // Prevent clicks on the toast from propagating to modal backdrops
  const handleToastClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  return (
    <div
      onClick={handleToastClick}
      className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full ${
        styles.bgColor
      } shadow-lg rounded-lg pointer-events-auto flex ring-1 ${styles.borderColor} overflow-hidden`}
    >
      <div className={`w-1.5 ${styles.accentColor}`}></div>
      <div className="flex-1 w-0 p-4">
        <div className="flex items-start">
          <div className={`flex-shrink-0 pt-0.5 ${styles.iconColor}`}>{icon || defaultIcon}</div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-semibold text-app-grey-90">{title}</p>
            {message && <p className="mt-1 text-xs text-app-grey-60">{message}</p>}
            {actionButton && (
              <div className="mt-2">
                <AppButton
                  size="xs"
                  variant="primary"
                  label={actionButton.label}
                  onClick={(e) => handleActionClick(e as React.MouseEvent)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex border-l border-app-grey-30">
        <button
          onClick={(e) => handleDismiss(e)}
          className="w-full border border-transparent rounded-none rounded-r-lg p-2 flex items-center justify-center text-app-grey-50 hover:text-app-grey-70 focus:outline-none transition-colors"
          aria-label="Close"
        >
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
